import { logger } from "@/lib/logger";
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Lock, Shield, CheckCircle2, XCircle } from "lucide-react";

interface ForcePasswordChangeProps {
  open: boolean;
  message?: string;
  onPasswordChanged: () => void;
  onDismiss?: () => void;
}

export function ForcePasswordChange({ 
  open, 
  message,
  onPasswordChanged,
  onDismiss
}: ForcePasswordChangeProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const passwordChecks = {
    length: newPassword.length >= 12,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[^A-Za-z0-9]/.test(newPassword),
    match: newPassword === confirmPassword && newPassword.length > 0
  };

  const allValid = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!allValid) {
      toast({
        title: "Invalid Password",
        description: "Please ensure all password requirements are met.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      
      if (error) {
        toast({
          title: "Password Update Failed",
          description: error.message,
          variant: "destructive"
        });
        return;
      }

      // Record the password change
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.rpc('record_password_change', { p_user_id: user.id });
      }

      toast({
        title: "Password Updated",
        description: "Your password has been successfully updated.",
      });
      
      onPasswordChanged();
    } catch (e) {
      logger.error('[ForcePasswordChange] Error:', e);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const CheckItem = ({ valid, label }: { valid: boolean; label: string }) => (
    <div className="flex items-center gap-2 text-sm">
      {valid ? (
        <CheckCircle2 className="h-4 w-4 text-green-500" />
      ) : (
        <XCircle className="h-4 w-4 text-muted-foreground" />
      )}
      <span className={valid ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'}>
        {label}
      </span>
    </div>
  );

  // Prevent closing the dialog by clicking outside or pressing escape
  const handleOpenChange = (isOpen: boolean) => {
    // Don't allow closing - user must change password
    if (!isOpen) return;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-full">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-xl">Password Update Required</DialogTitle>
          </div>
          <DialogDescription>
            {message || "Your password has expired. Please create a new password to continue."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mt-2">
          <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium">21 CFR Part 11 Compliance</p>
            <p className="mt-1 text-blue-700 dark:text-blue-300">
              Passwords must be changed every 90 days for regulatory compliance.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              autoComplete="new-password"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              autoComplete="new-password"
            />
          </div>

          <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
            <p className="text-sm font-medium mb-2">Password Requirements:</p>
            <div className="grid grid-cols-2 gap-2">
              <CheckItem valid={passwordChecks.length} label="12+ characters" />
              <CheckItem valid={passwordChecks.uppercase} label="Uppercase letter" />
              <CheckItem valid={passwordChecks.lowercase} label="Lowercase letter" />
              <CheckItem valid={passwordChecks.number} label="Number" />
              <CheckItem valid={passwordChecks.special} label="Special character" />
              <CheckItem valid={passwordChecks.match} label="Passwords match" />
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={!allValid || loading}
          >
            {loading ? "Updating..." : "Update Password"}
          </Button>

          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="w-full text-sm text-muted-foreground hover:text-foreground hover:underline mt-1"
            >
              Skip for now
            </button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
